import { createSelector, type OutputSelector } from 'reselect';
import mapValues from 'lodash/mapValues';
import pickBy from 'lodash/pickBy';
// eslint-disable-next-line lodash/import-scope
import type { Dictionary } from 'lodash';
import {
	FIELD_TYPES,
	FIELD_TYPES_CATEGORIES,
	getUnsupportedFieldsForPinning,
	UNSUPPORTED_STABLE_IDS_FOR_PINNING,
} from '@jira/polaris__domain-field/src/isSystemFieldType.tsx';
import type { FieldType } from '@jira/polaris__domain-field/src/FieldType.types.tsx';
import type { Field, FieldKey, FieldMap } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { State } from './FieldsUpdate.types.tsx';

export const getAllFields: OutputSelector<
	State,
	FieldMap,
	(
		res1: FieldMap,
		res2: boolean | undefined,
		res3: boolean | undefined,
		res4: boolean | undefined,
		res5: boolean | undefined,
	) => FieldMap
> = createSelector(
	(state: State): FieldMap => state.fields,
	(state: State) => state.containerProps?.isAtlasIntegrationEnabled,
	(state: State) => state.containerProps?.isCollectionView,
	(state: State) => state.containerProps?.isSharedView,
	(state: State) => state.containerProps?.isAnonymousUser,
	(fields, isAtlasIntegrationEnabled, isCollectionView, isSharedView) => {
		const shouldShowAtlasFields =
			!!isAtlasIntegrationEnabled ||
			!!isCollectionView ||
			(!!isSharedView && fg('jpd_support_atlas_fields_publish_views'));

		return shouldShowAtlasFields
			? fields
			: // oxlint-disable-next-line typescript/consistent-type-assertions
				(Object.keys(fields)
					.map((key) => fields[key])
					.filter((field) => {
						return !FIELD_TYPES_CATEGORIES.ATLAS.some((type) => type === field.type);
					})
					.reduce(
						(result, field) =>
							Object.assign(result, {
								[field.key]: field,
							}),
						{},
					) as FieldMap);
	},
);

export const getFields: OutputSelector<
	State,
	Dictionary<Field>,
	(res: FieldMap) => Dictionary<Field>
> = createSelector(getAllFields, (fields) =>
	pickBy(fields, (field) => {
		// Exclude archived fields
		if (FIELD_TYPES_CATEGORIES.ARCHIVED.some((type) => type === field.type)) {
			return false;
		}

		// Exclude atlas project fields based on feature flag
		if (fg('jpd_support_native_atlas_project_field')) {
			// When native field is enabled, exclude PLATFORM_PROJECT and ATLAS_PROJECT
			if (field.type === FIELD_TYPES.PLATFORM_PROJECT || field.type === FIELD_TYPES.ATLAS_PROJECT) {
				return false;
			}
		} else if (field.type === FIELD_TYPES.PLATFORM_ATLAS_PROJECT) {
			// When native field is disabled, exclude PLATFORM_ATLAS_PROJECT
			return false;
		}

		return true;
	}),
);

export const getAllFieldArray: OutputSelector<State, Field[], (res: FieldMap) => Field[]> =
	createSelector(getAllFields, (fields) => Object.values(fields));

export const getAllVisibleFields: OutputSelector<
	State,
	Dictionary<Field>,
	(res: FieldMap) => Dictionary<Field>
> = createSelector(getAllFields, (fields) =>
	pickBy(fields, (field) => {
		const isHidden = field.configuration?.hidden;
		const isRestricted = field.hasRestrictedContext;
		return !isHidden && !isRestricted;
	}),
);

export const getGlobalFieldsInUseArray: OutputSelector<State, Field[], (res: Field[]) => Field[]> =
	createSelector(getAllFieldArray, (fields) =>
		fields.filter((field) => field.global && !field.hasRestrictedContext),
	);

export const getNonGlobalFieldsInUseArray: OutputSelector<
	State,
	Field[],
	(res: Field[]) => Field[]
> = createSelector(getAllFieldArray, (fields) =>
	fields.filter((field) => !field.global && !field.hasRestrictedContext),
);

export const getFieldCount: OutputSelector<State, number, (res: Field[]) => number> =
	createSelector(getAllFieldArray, (fields) => fields.length);

export const getGlobalFieldCount: OutputSelector<State, number, (res: Field[]) => number> =
	createSelector(getAllFieldArray, (fields) => fields.filter((f) => f.global && f.custom).length);

export const getArchivedField: OutputSelector<
	State,
	Field | undefined,
	(res: Field[]) => Field | undefined
> = createSelector(getAllFieldArray, (fields) =>
	fields.find((f) => f.type === FIELD_TYPES.ARCHIVED && f.global),
);

export const getArchivedByField: OutputSelector<
	State,
	Field | undefined,
	(res: Field[]) => Field | undefined
> = createSelector(getAllFieldArray, (fields) =>
	fields.find((f) => f.type === FIELD_TYPES.ARCHIVED_BY && f.global),
);

export const getArchivedOnField: OutputSelector<
	State,
	Field | undefined,
	(res: Field[]) => Field | undefined
> = createSelector(getAllFieldArray, (fields) =>
	fields.find((f) => f.type === FIELD_TYPES.ARCHIVED_ON && f.global),
);

export const getIssueTypeField: OutputSelector<
	State,
	Field | undefined,
	(res: Field[]) => Field | undefined
> = createSelector(getAllFieldArray, (fields) =>
	fields.find((field) => field.type === FIELD_TYPES.ISSUE_TYPE),
);

export const getAtlasFieldsEnabled: OutputSelector<State, boolean, (res: FieldMap) => boolean> =
	createSelector(
		(state: State): FieldMap => state.fields,
		(fields) =>
			Object.keys(fields)
				.map((key) => fields[key])
				.find(
					(field) =>
						field.type === FIELD_TYPES.ATLAS_PROJECT || field.type === FIELD_TYPES.ATLAS_GOAL,
				) !== undefined,
	);

export const getNewlyAddedGlobalFields = (state: State): string[] => state.newlyAddedGlobalFields;

export const getArchivedFieldsConfig: OutputSelector<
	State,
	{
		archivedField: Field | undefined;
		archivedByField: Field | undefined;
		archivedOnField: Field | undefined;
	},
	(
		res1: Field | undefined,
		res2: Field | undefined,
		res3: Field | undefined,
	) => {
		archivedField: Field | undefined;
		archivedByField: Field | undefined;
		archivedOnField: Field | undefined;
	}
> = createSelector(
	getArchivedField,
	getArchivedByField,
	getArchivedOnField,
	(archivedField, archivedByField, archivedOnField) => ({
		archivedField,
		archivedByField,
		archivedOnField,
	}),
);

export const getIsArchivingEnabled: OutputSelector<
	State,
	boolean,
	(res1: Field | undefined, res2: Field | undefined, res3: Field | undefined) => boolean
> = createSelector(
	getArchivedField,
	getArchivedByField,
	getArchivedOnField,
	(archivedField, archivedByField, archivedOnField) =>
		archivedField !== undefined && archivedByField !== undefined && archivedOnField !== undefined,
);

export const getFieldLabels: OutputSelector<
	State,
	{
		[x: string]: string;
	},
	(res: Dictionary<Field>) => {
		[x: string]: string;
	}
> = createSelector(getFields, (fields) => mapValues(fields, (field) => field.label));

export const getNewFieldTypes: OutputSelector<
	State,
	{
		[x: string]: FieldType;
	},
	(res: Dictionary<Field>) => {
		[x: string]: FieldType;
	}
> = createSelector(getFields, (fields) => mapValues(fields, (field) => field.type));

export const getFieldArray: OutputSelector<State, Field[], (res: Dictionary<Field>) => Field[]> =
	createSelector(getFields, (fields) => Object.values(fields));

export const getAllStringFields: OutputSelector<State, Field[], (res: Field[]) => Field[]> =
	createSelector(getAllFieldArray, (fields) =>
		fields.filter((field) => field.type === FIELD_TYPES.SHORT_TEXT),
	);

export const getFieldTypeByKey: OutputSelector<
	State,
	Map<FieldKey, FieldType>,
	(res: Field[]) => Map<FieldKey, FieldType>
> = createSelector(getAllFieldArray, (fields) => {
	const map = new Map<FieldKey, FieldType>();

	for (const field of fields) {
		if (field.type !== undefined) {
			map.set(field.key, field.type);
		}
	}

	return map;
});

export const getFieldsForPinning: OutputSelector<State, Field[], (res: Field[]) => Field[]> =
	createSelector(getFieldArray, (fields) =>
		fields
			.filter(
				({ type, stableId }) =>
					!getUnsupportedFieldsForPinning().some((t) => t === type) &&
					!UNSUPPORTED_STABLE_IDS_FOR_PINNING.some((id) => stableId === id),
			)
			.sort((a, b) => a.label.localeCompare(b.label)),
	);
